import { IButtonWebProps } from "./types";
import {
  PolimorphicLinkAttributes,
  useMaybePolimorphicLinkAttributes,
} from "./usePolimorphicLinkAttributes";

type ButtonAttributes = {
  as: "button";
  linkProps: Record<string, never>;
};

type PolimorphicAttributes = ButtonAttributes | PolimorphicLinkAttributes;

// Adapted from core/web/src/slate/Button/utils
/**
 * Returns the appropriate attributes for the button based on the linkTo and other anchor props.
 *
 * If no linkTo or other anchor props are provided, this hook return `as="button"`.
 *
 * If the link has to be external, this hook returns `as={Anchor}` with the href set.
 *
 * Otherwise, this hook returns `as={Link}` with the `to` and `replace` set.
 *
 * @param props - The button props.
 * @returns The appropriate attributes for the button component.
 */
export const usePolimorphicAttributes = ({
  linkTo,
  ...props
}: IButtonWebProps): PolimorphicAttributes => {
  const polimorphicLinkAttributes = useMaybePolimorphicLinkAttributes({
    ...props,
    linkTo,
  });
  if (polimorphicLinkAttributes === undefined) {
    return { as: "button", linkProps: {} };
  }
  return polimorphicLinkAttributes;
};
